import { Language } from '@/models/Global'
import { useDefaultStore } from '@/store'
import { toComparable, toSafeString, transformString } from '../utils/tools'
import { TranslationNamespace } from '@/models/Translation'

export type TextTransform = 'capitalize' | 'uppercase' | 'lowercase'
export const AlphabeticalWordsRegex = /\b[a-z]+\b/gi

export function useTranslation() {
  const store = useDefaultStore()

  /**
   * Translates string if translation was found and optionally transforms it
   * listeners is attached to all translations and loaded in App.vue before rendering anything which makes it ok to use synchronously.
   * attempts translation of full string. use %s for dynamic string. customStrings are used in order, i.e. customStrings[0] will replace the first occurrence of %s etc.
   * @param str the english string to translate from. As a fallback (e.g. chosen translation language is english or translation not found), the entered capitalization will be used. for translation matching purposes, capitalization does not matter
   * @param translationNamespace the namespace or namespaces where to look for the translation
   * @param transform optionally transforms the output
   * @param overrideLanguage as default uses store.translationLanguage
   * @param customStrings replaces %s with these strings in order of appearance
   * @param onlyTranslateAlphabeticals excludes non-alphabetical characters when matching translation (e.g. "Calendar 2023" will look for a translation for "Calendar"). Non-alphabetical characters will be left as they are
   * @returns
   */
  const translate = (
    str: string | number | undefined | null,
    translationNamespace: TranslationNamespace | TranslationNamespace[],
    transform?: TextTransform,
    overrideLanguage?: Language,
    customStrings?: Array<string | number>,
    onlyTranslateAlphabeticals?: boolean,
    returnOriginalValueAsFallback = true, // return the original string if no translation were found, otherwise an empty string
  ): string => {
    let translation: string | undefined

    const getTranslation = (translationString: string, namespace: string) =>
      store.translations?.[namespace]?.[toComparable(translationString)]?.[overrideLanguage || store.translationLanguage]?.trim()

    try {
      const namespaces = Array.isArray(translationNamespace) ? translationNamespace : [translationNamespace]

      for (const namespace of namespaces) {
        if (!translation) {
          if (onlyTranslateAlphabeticals) {
            let translationFound = false

            const replacedString = str?.toString().replace(/[a-z/]+\s*[a-z/]+/i, (match) => {
              const partialTranslation = getTranslation(match, namespace)

              if (partialTranslation) {
                translationFound = true
              }

              return partialTranslation || match
            })

            if (translationFound || returnOriginalValueAsFallback) {
              translation = replacedString
            }
          } else {
            translation = getTranslation(toSafeString(str), namespace)
          }
        }
      }
    } catch (err) {
      console.log(str, translationNamespace, err)
    }

    if (translation || returnOriginalValueAsFallback) {
      const string = translation || toSafeString(str)?.trim()

      const transformedString = transformString(string, transform)

      let matchIndex = 0
      return transformedString.toString()?.replace(/%s/gi, () => toSafeString(customStrings?.[matchIndex++]))
    }

    return ''
  }

  const translateWords = (
    words: string,
    translationNamespace: TranslationNamespace | TranslationNamespace[],
    transform?: 'capitalize' | 'uppercase' | 'lowercase',
    overrideLanguage?: Language,
  ) =>
    words
      .split(' ')
      .map((word, i) => translate(word, translationNamespace, i === 0 ? transform : undefined, overrideLanguage))
      .join(' ')

  /**
   * Quick for translate(str, 'application', 'capitalize')
   */
  const appTranslate = (
    str: string | number | undefined,
    customStrings?: Array<string | number>,
    transform: TextTransform = 'capitalize',
    // overrideLanguage?: Language,
    onlyTranslateAlphabeticals?: boolean,
    returnOriginalValueAsFallback = true,
  ) => translate(str, 'application', transform, undefined, customStrings, onlyTranslateAlphabeticals, returnOriginalValueAsFallback)

  return {
    AlphabeticalWordsRegex,
    translate,
    translateWords,
    appTranslate,
  }
}
